import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { obterRede, salvarRede, type IntegracaoRede } from "../../../services/integracaoParceiros"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"

const inicial: IntegracaoRede = {
    pv: "",
    token: "",
    ambiente: "sandbox",
    softDescriptor: "",
    ativo: false,
}

// Verde SÓ quando ligado. O MUI v4 gera as classes de slot com sufixo numérico, então a cor
// tem que ir pela prop `classes` + refs $checked/$track — não por `style={{color}}`, que pinta
// a bolinha de verde inclusive no estado desligado (o que confundia: parecia ativo estando off).
const useSwitchVerdeStyles = makeStyles({
    switchBase: {
        "&$checked": { color: "#4caf50" },
        "&$checked + $track": { backgroundColor: "#4caf50" },
    },
    checked: {},
    track: {},
})

export function IntegracaoRedeForm() {
    const classes = useIntegracaoStyles()
    const sw = useSwitchVerdeStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dados, setDados] = useState<IntegracaoRede>(inicial)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const setCampo = <K extends keyof IntegracaoRede>(campo: K, valor: IntegracaoRede[K]) =>
        setDados(prev => ({ ...prev, [campo]: valor }))

    const aplicar = (res: IntegracaoRede) =>
        setDados({
            pv: res.pv ?? "",
            token: res.token ?? "",
            ambiente: res.ambiente === "producao" ? "producao" : "sandbox",
            softDescriptor: res.softDescriptor ?? "",
            ativo: res.ativo,
        })

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const res = await obterRede(autorizacao)
                if ("retornoErro" in res) {
                    alertStart(res.mensagem || "Erro ao carregar integração", "error")
                } else {
                    aplicar(res)
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSalvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        // Só exige credenciais quando a integração está sendo ativada.
        if (dados.ativo && (!dados.pv?.trim() || !dados.token?.trim())) {
            alertStart("Para ativar, preencha o PV e o Token da Rede.", "error")
            return
        }
        if (dados.softDescriptor && dados.softDescriptor.length > 18) {
            alertStart("O Soft Descriptor deve ter no máximo 18 caracteres.", "error")
            return
        }
        setLoading(true)
        try {
            const res = await salvarRede(autorizacao, dados)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
            } else {
                aplicar(res)
                alertStart("Configuração salva com sucesso!", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integração Rede
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Parâmetros Rede (cartão online)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="PV (número de afiliação)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.pv ?? ""}
                                    onChange={e => setCampo("pv", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Token (chave de integração)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    autoComplete="new-password"
                                    value={dados.token ?? ""}
                                    onChange={e => setCampo("token", e.target.value)}
                                    helperText="Credencial secreta fornecida pela Rede."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Ambiente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.ambiente}
                                    onChange={e => setCampo("ambiente", e.target.value)}
                                >
                                    <MenuItem value="sandbox">Sandbox (testes)</MenuItem>
                                    <MenuItem value="producao">Produção</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Soft Descriptor (opcional)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 18 }}
                                    value={dados.softDescriptor ?? ""}
                                    onChange={e => setCampo("softDescriptor", e.target.value)}
                                    helperText="Frase (até 18 caracteres) que aparece na fatura do cliente."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dados.ativo}
                                            onChange={e => setCampo("ativo", e.target.checked)}
                                            classes={{
                                                switchBase: sw.switchBase,
                                                checked: sw.checked,
                                                track: sw.track,
                                            }}
                                        />
                                    }
                                    label="Ativo"
                                />
                            </Grid>
                        </Grid>
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSalvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
